import React, { useMemo } from 'react';
import { arrayOf, bool, func, node, number, object, oneOf, shape, string } from 'prop-types';
import classNames from 'classnames';
import { FieldCustomSelect } from '..';
import css from './FieldPerferredLang.module.css';
import { languageSelectOptions, preferredLang } from '../../marketplace-custom-config';
import { oneOfType } from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as validators from '../../util/validators';

const customLabel = Object.values(languageSelectOptions).reduce((pre, curnt) => {
  pre[curnt.value] = string.isRequired;
  return pre;
}, {});

const nativeLangOptions = Object.keys(customLabel);

const FieldPerferredLangComponent = props => {
  const {
    rootClassName,
    className,
    errorClassName,
    id,
    onChange,
    options,
    isSearchable,
    userNativeLang,
    intl,
    noOptionMsgId,
    disabled,
    name,
    validator,
    titleClassName,
  } = props;

  const onChangeMaybe = typeof onChange === 'function' ? { onChange } : {};
  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(css.title, titleClassName);

  const preferredLangLabel = intl.formatMessage({ id: 'FieldPerferredLang.selectLabel' });
  const preferredLangPlaceholder = intl.formatMessage({
    id: 'FieldPerferredLang.selectPlaceholder',
  });
  const preferredLangRequiredMessage = intl.formatMessage({
    id: 'FieldPerferredLang.selectRequired',
  });
  const preferredLangRequired = validators.required(preferredLangRequiredMessage);
  const preferredLangValidator =
    typeof validator === 'function'
      ? validators.composeValidators(validator, preferredLangRequired)
      : preferredLangRequired;

  return (
    <div className={classes} id={id}>
      <div className={titleClasses}>
        <h2 className={css.heading}>
          <FormattedMessage id="FieldPerferredLang.heading" />
        </h2>
        <p className={css.helperText}>
          <FormattedMessage id="FieldPerferredLang.subHeading" />
        </p>
      </div>
      <div className={css.PreferredLangField}>
        <FieldCustomSelect
          id={id}
          name={name}
          userNativeLang={userNativeLang}
          options={options}
          label={preferredLangLabel}
          placeholder={preferredLangPlaceholder}
          className={className}
          errorClassName={errorClassName}
          validate={preferredLangValidator}
          isSearchable={isSearchable}
          disabled={disabled}
          noOptionMsgId={noOptionMsgId}
          {...onChangeMaybe}
        />
      </div>
    </div>
  );
};

FieldPerferredLangComponent.defaultProps = {
  rootClassName: null,
  className: null,
  errorClassName: null,
  id: null,
  label: null,
  isSearchable: false,
  userNativeLang: languageSelectOptions.en.value,
  noOptionMsgId: 'FieldPerferredLang.noOption',
  disabled: false,
};

FieldPerferredLangComponent.propTypes = {
  rootClassName: string,
  className: string,
  errorClassName: string,

  onChange: func,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string,
  name: string.isRequired,

  options: arrayOf(
    shape({
      value: string.isRequired,
      label: oneOfType([string, shape(customLabel)]).isRequired,
    })
  ).isRequired,
  isSearchable: bool.isRequired,
  userNativeLang: oneOf(nativeLangOptions).isRequired,
  noOptionMsgId: string.isRequired,
  disabled: bool.isRequired,
};

const FieldPerferredLangComp = injectIntl(FieldPerferredLangComponent);

const FieldPerferredLang = props => {
  const options = Object.values(preferredLang);
  return <FieldPerferredLangComp {...props} options={options} />;
};

FieldPerferredLang.defaultProps = {
  rootClassName: null,
  className: null,
  errorClassName: null,
  titleClassName: null,
  isSearchable: false,
  userNativeLang: languageSelectOptions.en.value,
  noOptionMsgId: 'FieldPerferredLang.noOption',
  disabled: false,
  validator: null,
};

FieldPerferredLang.propTypes = {
  rootClassName: string,
  className: string,
  errorClassName: string,
  titleClassName: string,

  onChange: func,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string.isRequired,
  name: string.isRequired,
  isSearchable: bool.isRequired,
  userNativeLang: oneOf(nativeLangOptions).isRequired,
  noOptionMsgId: string.isRequired,
  disabled: bool.isRequired,
  validator: func,
};

export default FieldPerferredLang;
